import axiosInstance from '../axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { clearPageCache, getPageCache, putPageCache } from '../../utils/cache';
import {
    DelegationMutationResponse,
    DelegationPayload,
    DelegationQueryResponse,
    DelegationResponse,
    MyActiveDelegationsResponse,
} from '../../type/Delegation';

const CACHE_KEY = 'delegation_list';

const createDelegation = async (
    payload: DelegationPayload,
): Promise<DelegationMutationResponse> => {
    const response: any = await axiosInstance.post<DelegationMutationResponse>(
        '/delegation/createDelegation',
        payload,
    );
    clearPageCache(CACHE_KEY);
    return response.data;
};

export const useCreateDelegation = () => {
    return useMutation<DelegationMutationResponse, Error, DelegationPayload>({
        mutationKey: ['createDelegation'],
        mutationFn: createDelegation,
    });
};

const revokeDelegation = async (
    payload: DelegationPayload,
): Promise<DelegationMutationResponse> => {
    const response: any = await axiosInstance.post<DelegationMutationResponse>(
        '/delegation/revokeDelegation',
        payload,
    );
    clearPageCache(CACHE_KEY);
    return response.data;
};

export const useRevokeDelegation = () => {
    return useMutation<DelegationMutationResponse, Error, DelegationPayload>({
        mutationKey: ['revokeDelegation'],
        mutationFn: revokeDelegation,
    });
};

const getDelegationById = async (
    payload: DelegationPayload,
): Promise<DelegationQueryResponse> => {
    if (getPageCache(CACHE_KEY, payload)) {
        return getPageCache(CACHE_KEY, payload);
    }
    const response: any = await axiosInstance.post<DelegationMutationResponse>(
        '/delegation/getDelegationById',
        payload,
    );
    putPageCache(CACHE_KEY, payload, response.data.data);
    return response.data.data;
};

export const useGetDelegationById = () => {
    return useMutation<DelegationQueryResponse, Error, DelegationPayload>({
        mutationKey: ['getDelegationById'],
        mutationFn: getDelegationById,
    });
};

const listDelegations = async (payload: DelegationPayload): Promise<DelegationResponse> => {
    if (getPageCache(CACHE_KEY, payload)) {
        return getPageCache(CACHE_KEY, payload);
    }
    const response: any = await axiosInstance.post<DelegationMutationResponse>(
        '/delegation/listDelegations',
        payload,
    );
    putPageCache(CACHE_KEY, payload, response.data.data);
    return response.data.data;
};

export const useListDelegations = (payload: DelegationPayload) => {
    return useQuery<DelegationResponse, Error>({
        queryKey: ['listDelegations', payload],
        queryFn: () => listDelegations(payload),
        enabled: !!payload,
    });
};

const getMyActiveDelegations = async (
    payload: DelegationPayload,
): Promise<MyActiveDelegationsResponse> => {
    const response: any = await axiosInstance.post<MyActiveDelegationsResponse>(
        '/delegation/getMyActiveDelegations',
        payload,
    );
    return response.data;
};

export const useGetMyActiveDelegations = () => {
    return useMutation<MyActiveDelegationsResponse, Error, DelegationPayload>({
        mutationKey: ['getMyActiveDelegations'],
        mutationFn: getMyActiveDelegations,
    });
};
